import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Wrapper for pages/views that need a width NARROWER than the app-wide cap
 * (`max-w-[1600px]` applied globally by `AppLayout`).
 *
 * Use this instead of declaring `mx-auto max-w-*` inline on the outer
 * element of a page. Variants are a closed enum — if a new width is
 * needed, add a variant here. Do NOT reintroduce inline `max-w-*`.
 *
 * If a page works fine at the app-wide cap (dashboards, grids, tables,
 * listings), it does NOT need to use `PageContainer` at all.
 */
export type PageContainerVariant =
  | 'wide'     // max-w-6xl (1152px): single-column chat
  | 'content'  // max-w-4xl (896px): detail views with cards/data
  | 'reading'  // max-w-3xl (768px): long-form articles
  | 'focus';   // max-w-2xl (672px): briefings, forms, empty states, CTAs

type PageContainerAs = 'div' | 'section' | 'main' | 'article';

interface PageContainerProps {
  children: ReactNode;
  variant: PageContainerVariant;
  className?: string;
  as?: PageContainerAs;
}

const VARIANT_CLASS: Record<PageContainerVariant, string> = {
  wide: 'mx-auto max-w-6xl',
  content: 'mx-auto max-w-4xl',
  reading: 'mx-auto max-w-3xl',
  focus: 'mx-auto max-w-2xl',
};

export default function PageContainer({
  children,
  variant,
  className,
  as: Tag = 'div',
}: PageContainerProps) {
  return (
    <Tag className={cn(VARIANT_CLASS[variant], className)}>
      {children}
    </Tag>
  );
}
